import { initializeApp, type FirebaseApp } from 'firebase/app';
import { GithubAuthProvider, GoogleAuthProvider, getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { ReCaptchaV3Provider, initializeAppCheck } from 'firebase/app-check';
import { getAnalytics, isSupported as analyticsSupported, type Analytics } from 'firebase/analytics';

const env = import.meta.env;

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
};

/** True once real project keys are present in .env — the UI degrades gracefully without them. */
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

let app: FirebaseApp | null = null;

if (isFirebaseConfigured) {
  app = initializeApp(firebaseConfig);

  // App Check (reCAPTCHA v3) — our CAPTCHA + abuse-protection layer.
  if (env.VITE_RECAPTCHA_SITE_KEY) {
    if (env.DEV && env.VITE_APPCHECK_DEBUG_TOKEN) {
      (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        env.VITE_APPCHECK_DEBUG_TOKEN;
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(env.VITE_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }
}

function requireApp(): FirebaseApp {
  if (!app) {
    throw new Error('Firebase is not configured. Copy .env.example to .env and add your project keys.');
  }
  return app;
}

export function firebaseAuth(): Auth {
  return getAuth(requireApp());
}

export function db(): Firestore {
  return getFirestore(requireApp());
}

export function fns(): Functions {
  return getFunctions(requireApp(), 'us-central1');
}

let analytics: Analytics | null = null;

export function analyticsInstance(): Analytics | null {
  return analytics;
}

export async function initAnalytics(): Promise<void> {
  if (!app || !firebaseConfig.measurementId) return;
  try {
    if (await analyticsSupported()) analytics = getAnalytics(app);
  } catch {
    // Analytics unsupported in this environment (e.g. some in-app browsers).
  }
}

export const googleProvider = new GoogleAuthProvider();
export const githubProvider = new GithubAuthProvider();
